import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import bookingService from '../../services/bookingService';

const ManualBookingDialog = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    // Customer details
    customerName: '',
    customerEmail: '',
    customerPhone: '',

    // Vehicle details
    vehicleRegistration: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleFuel: '',
    vehicleTransmission: '',
    vehicleEngineSize: '',

    // Booking details
    bookingDate: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
    bookingTime: '',
    status: 'confirmed',

    // Services
    services: [{ service: '', cost: '' }],

    // Notes
    notes: '',
  });

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleDateChange = (event) => {
    setFormData({
      ...formData,
      bookingDate: event.target.value,
    });
  };

  const handleServiceChange = (index, field) => (event) => {
    const newServices = [...formData.services];
    newServices[index][field] = event.target.value;
    setFormData({
      ...formData,
      services: newServices,
    });
  };

  const handleAddService = () => {
    setFormData({
      ...formData,
      services: [...formData.services, { service: '', cost: '' }],
    });
  };

  const handleRemoveService = (index) => {
    const newServices = formData.services.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      services: newServices.length > 0 ? newServices : [{ service: '', cost: '' }],
    });
  };

  const calculateTotalCost = () => {
    return formData.services.reduce((total, service) => {
      const cost = parseFloat(service.cost) || 0;
      return total + cost;
    }, 0);
  };

  const validateForm = () => {
    if (!formData.customerName.trim()) {
      setError('Customer name is required');
      return false;
    }
    if (!formData.vehicleRegistration.trim()) {
      setError('Vehicle registration is required');
      return false;
    }
    if (!formData.bookingDate) {
      setError('Booking date is required');
      return false;
    }

    // Validate at least one service with valid cost
    const hasValidService = formData.services.some(
      service => service.service.trim() && !isNaN(parseFloat(service.cost))
    );

    if (!hasValidService) {
      setError('At least one service with a valid cost is required');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Format services
      const workRequired = formData.services
        .filter(s => s.service.trim() && !isNaN(parseFloat(s.cost)))
        .map(s => ({
          service: s.service.trim(),
          cost: parseFloat(s.cost),
        }));

      // Prepare booking data
      const bookingData = {
        customer: {
          name: formData.customerName.trim(),
          email: formData.customerEmail.trim(),
          phone: formData.customerPhone.trim(),
        },
        vehicle: {
          registration: formData.vehicleRegistration.trim().toUpperCase(),
          make_model: `${formData.vehicleMake.trim()} ${formData.vehicleModel.trim()}`.trim() || 'Unknown',
          fuel: formData.vehicleFuel,
          transmission: formData.vehicleTransmission,
          engine_size: formData.vehicleEngineSize,
        },
        booking_date: {
          date: formData.bookingDate, // Already in YYYY-MM-DD format
          time: formData.bookingTime,
        },
        work_required: workRequired,
        total_cost: calculateTotalCost(),
        notes: formData.notes.trim(),
        status: formData.status,
      };

      const response = await bookingService.createManualBooking(bookingData);

      // Success
      if (onSuccess) {
        onSuccess(response);
      }

      handleClose();
    } catch (err) {
      console.error('Error creating booking:', err);
      setError(err.response?.data?.error || 'Failed to create booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      // Reset form
      setFormData({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        vehicleRegistration: '',
        vehicleMake: '',
        vehicleModel: '',
        vehicleFuel: '',
        vehicleTransmission: '',
        vehicleEngineSize: '',
        bookingDate: new Date().toISOString().split('T')[0],
        bookingTime: '',
        status: 'confirmed',
        services: [{ service: '', cost: '' }],
        notes: '',
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Create Manual Booking</Typography>
          <IconButton onClick={handleClose} disabled={loading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
          {/* Customer Information */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Customer Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Customer Name"
                value={formData.customerName}
                onChange={handleInputChange('customerName')}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.customerEmail}
                onChange={handleInputChange('customerEmail')}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.customerPhone}
                onChange={handleInputChange('customerPhone')}
                disabled={loading}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Vehicle Information */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Vehicle Information
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Registration Number"
                value={formData.vehicleRegistration}
                onChange={handleInputChange('vehicleRegistration')}
                disabled={loading}
                inputProps={{ style: { textTransform: 'uppercase' } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Make"
                value={formData.vehicleMake}
                onChange={handleInputChange('vehicleMake')}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                label="Model"
                value={formData.vehicleModel}
                onChange={handleInputChange('vehicleModel')}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Fuel Type</InputLabel>
                <Select
                  value={formData.vehicleFuel}
                  label="Fuel Type"
                  onChange={handleInputChange('vehicleFuel')}
                  disabled={loading}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  <MenuItem value="Petrol">Petrol</MenuItem>
                  <MenuItem value="Diesel">Diesel</MenuItem>
                  <MenuItem value="Electric">Electric</MenuItem>
                  <MenuItem value="Hybrid">Hybrid</MenuItem>
                  <MenuItem value="LPG">LPG</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Transmission</InputLabel>
                <Select
                  value={formData.vehicleTransmission}
                  label="Transmission"
                  onChange={handleInputChange('vehicleTransmission')}
                  disabled={loading}
                >
                  <MenuItem value="">Not specified</MenuItem>
                  <MenuItem value="Manual">Manual</MenuItem>
                  <MenuItem value="Automatic">Automatic</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Engine Size"
                value={formData.vehicleEngineSize}
                onChange={handleInputChange('vehicleEngineSize')}
                disabled={loading}
                placeholder="e.g., 1.6L"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Booking Information */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Booking Details
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                label="Booking Date"
                type="date"
                value={formData.bookingDate}
                onChange={handleDateChange}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={formData.bookingTime}
                onChange={handleInputChange('bookingTime')}
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleInputChange('status')}
                  disabled={loading}
                >
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="assigned">Assigned</MenuItem>
                  <MenuItem value="pending_admin_review">Pending Review</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Services */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              Services Required
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddService}
              disabled={loading}
            >
              Add Service
            </Button>
          </Box>

          {formData.services.map((service, index) => (
            <Grid container spacing={2} key={index} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  label="Service Description"
                  value={service.service}
                  onChange={handleServiceChange(index, 'service')}
                  disabled={loading}
                  placeholder="e.g., Oil Change, MOT Test"
                />
              </Grid>
              <Grid item xs={10} sm={4}>
                <TextField
                  fullWidth
                  label="Cost"
                  type="number"
                  value={service.cost}
                  onChange={handleServiceChange(index, 'cost')}
                  disabled={loading}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={2} sm={1}>
                <IconButton
                  onClick={() => handleRemoveService(index)}
                  disabled={loading || formData.services.length === 1}
                  color="error"
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="h6" align="right">
              Total Cost: £{calculateTotalCost().toFixed(2)}
            </Typography>
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Notes */}
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Additional Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={formData.notes}
            onChange={handleInputChange('notes')}
            disabled={loading}
            placeholder="Any additional information about the booking..."
          />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Creating...' : 'Create Booking'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualBookingDialog;
